import { GridColDef } from '@mui/x-data-grid';
import { CpgDimension } from '../../../api/schema';
import { OptimizationIndexChip } from '@features/campaigns/components/OptimizationIndexChip';
import { ConfidenceLevel } from '@features/campaigns/components/ConfidenceLevel';
import { formatters } from '@utils/formattersV2';
import { Typography, Stack } from '@mui/material';
import {
  OptimizationSelect,
  OptimizationSelectProps,
  Option as BaseOption,
} from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelect';
import { formatAveragePurchaseFrequency } from '../../../domain/formatters';
import { SignificanceIcon } from '@components/shared/SignificanceIcon';
import { sanitizeOptimization } from '@utils/valueTransformers';
import { useFeatures } from '@utils/hooks';

export type OptimizationIndex = keyof Pick<
  CpgDimension,
  | 'salesLiftIndex'
  | 'incrementalSalesPerImpressionIndex'
  | 'salesPerImpressionIndex'
>;

type Option = BaseOption & {
  value: OptimizationIndex;
};

const optimizationIndexOptions: Record<Option['value'], Option> = {
  salesLiftIndex: {
    value: 'salesLiftIndex',
    name: 'Opt. Index - Lift',
  },
  incrementalSalesPerImpressionIndex: {
    value: 'incrementalSalesPerImpressionIndex',
    name: 'Opt. Index - Incremental Sales Per Impression',
  },
  salesPerImpressionIndex: {
    value: 'salesPerImpressionIndex',
    name: 'Opt. Index - Total Sales Per Impression',
  },
};

type ColumnsOptions = {
  optimizationIndex: OptimizationIndex;
  setOptimizationIndex: OptimizationSelectProps<Option>['setSelectedOptimization'];
};

export type MinimalCpgDimension = Pick<
  CpgDimension,
  | 'id'
  | 'dimensionValue'
  | 'statisticallySignificant'
  | 'salesLiftIndex'
  | 'incrementalSalesPerImpressionIndex'
  | 'salesPerImpressionIndex'
  | 'impressions'
  | 'uniqueUsers'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'incrementalSalesLiftConfidenceLevel'
  | 'totalProjectedSales'
  | 'averagePricePerTransaction'
  | 'averageTotalBasketAmount'
  | 'penetration'
  | 'averagePurchaseFrequency'
  | 'salesRate'
>;

type Column = GridColDef<MinimalCpgDimension>;

export const columns = (
  options: ColumnsOptions,
  features: ReturnType<typeof useFeatures>
) =>
  [
    {
      field: 'statisticallySignificant',
      headerName: '',
      flex: 0.2,
      minWidth: 40,
      renderCell: ({ row }) => (
        <SignificanceIcon
          status={
            row.statisticallySignificant ? 'significant' : 'insignificant'
          }
        />
      ),
    },
    {
      field: 'dimensionValue',
      headerName: 'Dimension',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'optimizationIndex',
      headerName: 'Optimization Index',
      flex: 1,
      minWidth: 400,
      valueGetter: (_, row) =>
        sanitizeOptimization(row[options.optimizationIndex]),
      renderHeader: () => (
        <OptimizationSelect
          selectedOptimization={
            optimizationIndexOptions[options.optimizationIndex]
          }
          setSelectedOptimization={options.setOptimizationIndex}
          options={Object.values(optimizationIndexOptions)}
        />
      ),
      renderCell: ({ row }) => (
        <OptimizationIndexChip
          value={sanitizeOptimization(row[options.optimizationIndex])}
        />
      ),
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      flex: 1,
      minWidth: 123,
      valueFormatter: value => formatters.impressions(value, { full: true }),
    },
    {
      field: 'uniqueUsers',
      headerName: 'Unique Users',
      flex: 1,
      minWidth: 132,
      valueFormatter: value => formatters.users(value, { full: true }),
    },
    {
      field: 'projectedIncrementalSales',
      headerName: 'Proj. Incr. Sales',
      flex: 1,
      minWidth: 145,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
    },
    {
      field: 'incrementalSalesLift',
      headerName: 'Incr. Sales Lift',
      display: 'flex',
      flex: 1,
      type: 'number',
      minWidth: 137,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>
            {formatters.lift(row.incrementalSalesLift, { decimals: 1 })}
          </Typography>
          {features.cpgAdditionalMetrics ? (
            <ConfidenceLevel
              value={row.incrementalSalesLiftConfidenceLevel}
              lift={row.incrementalSalesLift}
            />
          ) : null}
        </Stack>
      ),
      valueFormatter: value => formatters.lift(value, { decimals: 1 }),
    },
    {
      field: 'totalProjectedSales',
      headerName: 'Total Proj. Sales',
      flex: 1,
      minWidth: 150,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
    },
    {
      field: 'averagePricePerTransaction',
      headerName: 'Avg. Price Per Transaction',
      flex: 1,
      minWidth: 220,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 2 }),
    },
    {
      field: 'averageTotalBasketAmount',
      headerName: 'Avg. Total Basket Amount',
      flex: 1,
      minWidth: 244,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 2 }),
    },
    {
      field: 'penetration',
      headerName: 'Penetration',
      flex: 1,
      minWidth: 148,
      valueFormatter: value => formatters.percentage(value, { decimals: 2 }),
    },
    {
      field: 'averagePurchaseFrequency',
      headerName: 'Avg. Purchase Frequency',
      flex: 1,
      minWidth: 242,
      valueFormatter: formatAveragePurchaseFrequency,
    },
    {
      field: 'salesRate',
      headerName: 'Sales Rate',
      flex: 1,
      minWidth: 140,
      valueFormatter: value => formatters.currency(value, { decimals: 2 }),
    },
  ] as const satisfies readonly Column[];
